<script setup lang="ts">
import { UiCard, UiButton, UiText, UiIcon, UiImage, HiredOnShopexpertsBadge, UiChip, type CustomerStoryCard as ExpertCustomerStoryCard } from "@shopexperts/shared";
import DragHandle from "@/components/common/DragHandle.vue";
import { computed } from "vue";

const props = defineProps<{
  item: ExpertCustomerStoryCard;
  index?: number;
}>();

const emit = defineEmits<{
  (e: 'edit'): void;
  (e: 'delete'): void;
}>();

function onEditClick() {
  emit('edit');
}

function onDeleteClick() {
  emit('delete');
}

const orderedImages = computed(() => {
  const images: string[] = [];

  if (props.item.thumbnail) {
    images.push(props.item.thumbnail);
  }

  if (props.item.gallery_images?.length) {
    images.push(...props.item.gallery_images.filter(Boolean));
  }

  return images;
});

const heroImage = computed(() => {
  if (props.item.thumbnail) {
    return props.item.thumbnail;
  }

  return props.item.gallery_images?.find(Boolean) || "";
});

const galleryImages = computed(() => {
  if (props.item.thumbnail) {
    return props.item.gallery_images?.filter(Boolean) || [];
  }

  return orderedImages.value.slice(1);
});

// Format period from dates
const period = computed(() => {
  if (!props.item.start_date && !props.item.end_date) return '';

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length < 2) return '';
    const year = parts[0];
    const month = parts[1];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month || '1') - 1;
    return `${monthNames[monthIndex]} ${year}`;
  };

  const start = formatDate(props.item.start_date ?? undefined);
  const end = formatDate(props.item.end_date ?? undefined);

  if (start && end) return `${start} – ${end}`;
  if (start) return `${start} – Present`;
  return '';
});

</script>

<template>
  <UiCard padding="lg" class="relative draggable-item">
    <div class="flex flex-col gap-8">
      <div class="flex items-start justify-between mb-2">
        <div class="flex flex-col items-start gap-4">
          <HiredOnShopexpertsBadge :hiredOnShopexperts="props.item.hired_at_shopexperts || false" />
          <div class="flex flex-col gap-2">
            <UiText as="h3" variant="h5" class="font-semibold">{{ props.item.title }}</UiText>
            <UiText v-if="period" as="h6" class="text-se-gray-700" :text="period" />
          </div>
        </div>
        <div class="flex items-center gap-4">
          <UiChip
            :text="props.item.status === 'published' ? 'Published' : 'Draft'"
            :variant="props.item.status === 'published' ? 'success' : 'neutral'"
            size="sm"
          />
          <UiButton variant="outline" shape="icon" @click="onEditClick" no-py class="p-1.5">
            <template #before_label>
              <UiIcon name="pencil" class="w-4.5 h-4.5" />
            </template>
          </UiButton>
          <UiButton variant="outline" shape="icon" @click="onDeleteClick" no-py class="p-1.5">
            <template #before_label>
              <UiIcon name="delete" class="w-4.5 h-4.5" />
            </template>
          </UiButton>
          <DragHandle />
        </div>
      </div>
      <div class="flex flex-col gap-4 pr-6">
        <div class="relative w-full aspect-5/3 md:aspect-10/4 overflow-hidden rounded-xl ring-1 ring-se-gray-300">
          <UiImage
            v-if="heroImage"
            :src="heroImage"
            :alt="props.item.title"
            :rounded="false"
            :cover="true"
            class="w-full h-full"
          />
          <div v-else class="w-full h-full bg-se-gray-100" />
        </div>

        <div v-if="galleryImages.length" class="flex gap-3 overflow-x-auto snap-x snap-mandatory">
          <div
            v-for="(image, index) in galleryImages"
            :key="`${image}-${index}`"
            class="snap-start shrink-0 w-28 md:w-32"
          >
            <div class="w-full aspect-video overflow-hidden rounded-lg border border-se-gray-300">
              <UiImage
                :src="image"
                :alt="`${props.item.title} gallery image ${index + 1}`"
                :rounded="false"
                :cover="true"
                class="w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </UiCard>
</template>
